import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import BranchTeamProvider from "./BranchTeamProvider";
import { SearchTeamList } from "../../../model/branch/BranchTeamModel";
import { addModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";
import { useDispatch } from "react-redux";
import { updateTeam } from "../../../libraries/state_reducers/widget/DetailConnectDeptTeamReducer";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

type BranchTeamContextType = {
  branchTeamList: Array<SearchTeamList> | null;
  branchTeamInit: () => void;
  onClickTeam: (detail: SearchTeamList) => void;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;
};

const BranchTeamContext = createContext<BranchTeamContextType | undefined>(
  undefined
);

export const BranchTeamContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const searchValueRef = useRef<string>("");
  const branchTeamResultRef = useRef<Array<SearchTeamList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [branchTeamList, setBranchTeamList] =
    useState<Array<SearchTeamList> | null>(null);

  const branchTeamInit = async () => {
    const branchTeamModel = await BranchTeamProvider.getTeamListAPI();
    if (branchTeamModel !== null) {
      const teamListResult: Array<SearchTeamList> | null =
        branchTeamModel?.result?.search_team_list!;

      setBranchTeamList(teamListResult);
      branchTeamResultRef.current = teamListResult;
    }
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-branch-team`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<SearchTeamList> | undefined =
        branchTeamResultRef.current?.filter((team) =>
          Object.values(team)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setBranchTeamList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }
    setBranchTeamList(branchTeamResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  const onClickTeam = (detail: SearchTeamList) => {
    dispatch(updateTeam(detail));
    dispatch(
      addModal({
        id: ModalKeys.connect_detail_branch_team,
        title: detail?.name!,
        size: "md",
        allowBottomSheet: true,
        hideHeader: true,
        bottomSheetHideTitle: true,
      })
    );
  };

  return (
    <BranchTeamContext.Provider
      value={{
        branchTeamList,
        branchTeamInit,
        onClickTeam,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,
      }}
    >
      {children}
    </BranchTeamContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const BranchTeamContextImport = () => {
  const context = useContext(BranchTeamContext);
  if (!context) {
    throw new Error(
      "BranchTeamContextImport must be used within a BranchTeamContextProvider"
    );
  }
  return context;
};
